import { ConnectedPlumaProvider } from '@/components/connected-pluma-provider';
import { DevHelper } from '@/components/dev-helper';
import { ActiveAppContextProvider } from '@/context/active-bundle';
import { BackgroundAppBundleContextProvider } from '@/context/background-app-bundle';
import { NavigationContextProvider } from '@/context/navigation';
import { SafeNavigateProvider, useSafeNavigate } from '@/context/safe-navigate';
import { ThemeContextProvider } from '@/context/theme';
import { getSampleAccounts } from '@/features/workspaces/hooks';
import { useInjectImportShim } from '@/hooks/use-inject-import-shim';
import { useLoadShims } from '@/hooks/use-load-shims';
import { authStore } from '@/stores/auth';
import type { AppBundle } from '@/utils/app-bundles';
import tracking from '@/utils/tracking-scripts';
import { activeRouteStore, subscribeToHydraNavigate } from '@customerio/hydra-nav-state';
import { Box, PageLoader } from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LazyMotion, domAnimation } from 'framer-motion';
import type { ReactNode } from 'react';
import { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, UNSAFE_DataRouterContext } from 'react-router-dom';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const prefetchSampleAccounts = async () => {
  await queryClient.prefetchQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });
};

export default function Root() {
  const loaded = useLoadShims();

  useEffect(() => {
    prefetchSampleAccounts();
  }, []);

  return (
    <LazyMotion features={domAnimation}>
      <QueryClientProvider client={queryClient}>
        <ActiveAppContextProvider>
          <BackgroundAppBundleContextProvider>
            <SafeNavigateProvider>
              <NavigationContextProvider>
                <ThemeContextProvider>
                  <ConnectedPlumaProvider>{loaded ? <App /> : null}</ConnectedPlumaProvider>
                </ThemeContextProvider>
              </NavigationContextProvider>
            </SafeNavigateProvider>
          </BackgroundAppBundleContextProvider>
        </ActiveAppContextProvider>
      </QueryClientProvider>
    </LazyMotion>
  );
}

function AppContainer({ children }: { children?: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: 'var(--pluma-palette-grey-050)',
      }}
    >
      <Box id="in-app-target_root-announcement" flexShrink="0" />
      {children}

      <DevHelper />
    </div>
  );
}

function App() {
  const dataRouterContext = useContext(UNSAFE_DataRouterContext);

  const safeNavigate = useSafeNavigate();

  const isLoaded = useInjectImportShim();
  const activeRoute = useStore(activeRouteStore);

  /**
   * We use this code here to track users on injected
   * scripts as soon as they are authenticated
   * (as opposed to waiting for a user
   * to navigate to an authenticated URL)
   */
  const auth = useStore(authStore);
  const hasInjectedScripts = useRef<boolean>(false);
  const { account, user } = auth;
  useEffect(() => {
    if (account !== null && user !== null && !hasInjectedScripts.current) {
      // We only ever want to inject once.
      hasInjectedScripts.current = true;
      tracking.load({ authenticated: true, account, user });
    }
  }, [account, user]);

  // uses useLayoutEffect, rather than useEffect, to ensure this runs before
  // other effects have a chance to run, or components have a chance to render.
  useLayoutEffect(() => {
    const router = dataRouterContext?.router;

    if (activeRoute != null && activeRoute !== '' && router != null) {
      // Force React Router's internal location state to update without
      // triggering a navigation (that would add browser history entries, which
      // we're trying to avoid).
      // This is so that embedded app navigation gets synchronized with Hydra
      // UI's location state whenever a route changes.
      const [pathname, search] = activeRoute.split('?');
      router.state.location.pathname = pathname;
      router.state.location.search = search != null ? `?${search}` : '';
      router.revalidate();
    }
  }, [activeRoute]);

  useEffect(() => {
    return subscribeToHydraNavigate((event) => {
      safeNavigate(event.detail.url, {
        shouldFadeOut: event.detail.options?.shouldFadeOut,
        shouldUnrenderOutlet: event.detail.options?.shouldUnrenderOutlet,
        shouldReplaceHistoryEntry: event.detail.options?.shouldReplaceHistoryEntry,
        targetApp: event.detail.options?.targetApp as AppBundle | undefined,
      });
    });
  }, [safeNavigate]);

  return <AppContainer>{isLoaded ? <Outlet /> : <PageLoader />}</AppContainer>;
}
